import { z } from 'zod';
export const MessageCategorySchema = z.enum([
    'agent:start',
    'agent:progress',
    'agent:complete',
    'agent:error',
    'orchestrator:start_pipeline',
    'orchestrator:dispatch_agent',
    'orchestrator:stop_pipeline',
    'claude:start',
    'claude:progress',
    'claude:complete',
    'claude:error',
    'tool:start',
    'tool:progress',
    'tool:success',
    'tool:error',
    'session:start',
    'session:stop',
    'agent_switch',
    'command_list',
    'command_history',
    'user_prompt',
    'assistant_response',
    'system_info',
    'system_error'
]);
export const MessageSeveritySchema = z.enum(['info', 'success', 'warning', 'error']);
export const MessageMetadataSchema = z.object({
    duration: z.number().optional(),
    percentage: z.number().min(0).max(100).optional(),
    toolCount: z.number().optional(),
    tokens: z.number().optional(),
    cost: z.number().optional(),
    modelName: z.string().optional(),
    error: z.string().optional(),
}).catchall(z.any());
export const UIMessageSchema = z.object({
    id: z.string().uuid(),
    type: z.enum(['user', 'assistant', 'system']),
    content: z.string(),
    timestamp: z.date(),
    agentName: z.string().optional(),
    category: MessageCategorySchema.optional(),
    severity: MessageSeveritySchema.optional(),
    metadata: MessageMetadataSchema.optional(),
    groupId: z.string().uuid().optional(),
    parentId: z.string().uuid().optional(),
    isGroupHeader: z.boolean().optional(),
    isCollapsed: z.boolean().optional(),
    children: z.array(z.lazy(() => UIMessageSchema)).optional(),
});
export function getMessageIcon(message) {
    if (message.type === 'user') {
        return '👤';
    }
    if (message.type === 'assistant') {
        return '🤖';
    }
    if (message.category) {
        switch (message.category) {
            case 'agent:start':
                return '🚀';
            case 'agent:progress':
                return '⏳';
            case 'agent:complete':
                return message.severity === 'success' ? '✅' : '⚙️';
            case 'agent:error':
                return '❌';
            case 'orchestrator:start_pipeline':
                return '🔀';
            case 'orchestrator:dispatch_agent':
                return '📤';
            case 'orchestrator:stop_pipeline':
                return '🏁';
            case 'claude:start':
                return '🤖';
            case 'claude:progress':
                return '💭';
            case 'claude:complete':
                return '✅';
            case 'claude:error':
                return '❌';
            case 'tool:start':
                return '🔧';
            case 'tool:progress':
                return '⚙️';
            case 'tool:success':
                return '✅';
            case 'tool:error':
                return '❌';
            case 'session:start':
                return '🎬';
            case 'session:stop':
                return '🛑';
            case 'agent_switch':
                return '🔄';
            case 'command_list':
                return '📋';
            case 'command_history':
                return '📜';
            case 'system_info':
                return '💡';
            case 'system_error':
                return '⚠️';
            case 'user_prompt':
                return '👤';
            case 'assistant_response':
                return '🤖';
            default:
                return '⚙️';
        }
    }
    if (message.severity) {
        switch (message.severity) {
            case 'success':
                return '✅ ';
            case 'warning':
                return '⚠️ ';
            case 'error':
                return '❌ ';
            default:
                return '⚙️ ';
        }
    }
    return '⚙️';
}
export class MessageBuilder {
    message = {};
    constructor() {
        this.message = {
            timestamp: new Date(),
        };
    }
    withId(id) {
        this.message.id = id;
        return this;
    }
    withType(type) {
        this.message.type = type;
        return this;
    }
    withContent(content) {
        this.message.content = content;
        return this;
    }
    withAgent(agentName) {
        this.message.agentName = agentName;
        return this;
    }
    withCategory(category) {
        this.message.category = category;
        return this;
    }
    withSeverity(severity) {
        this.message.severity = severity;
        return this;
    }
    withMetadata(metadata) {
        this.message.metadata = { ...this.message.metadata, ...metadata };
        return this;
    }
    withDuration(duration) {
        if (!this.message.metadata)
            this.message.metadata = {};
        this.message.metadata.duration = duration;
        return this;
    }
    withPercentage(percentage) {
        if (!this.message.metadata)
            this.message.metadata = {};
        this.message.metadata.percentage = percentage;
        return this;
    }
    withModelName(modelName) {
        if (!this.message.metadata)
            this.message.metadata = {};
        this.message.metadata.modelName = modelName;
        return this;
    }
    withGroupId(groupId) {
        this.message.groupId = groupId;
        return this;
    }
    withTimestamp(timestamp) {
        this.message.timestamp = timestamp;
        return this;
    }
    build() {
        if (!this.message.id) {
            this.message.id = crypto.randomUUID();
        }
        if (!this.message.type) {
            throw new Error('Message type is required');
        }
        if (!this.message.content) {
            throw new Error('Message content is required');
        }
        return UIMessageSchema.parse(this.message);
    }
}
export function createMessage() {
    return new MessageBuilder();
}
export function createUserMessage(content) {
    return createMessage()
        .withType('user')
        .withContent(content)
        .withCategory('user_prompt')
        .withSeverity('info')
        .build();
}
export function createAssistantResponse(agentName, content) {
    return createMessage()
        .withType('assistant')
        .withContent(content)
        .withAgent(agentName)
        .withCategory('assistant_response')
        .withSeverity('success')
        .build();
}
//# sourceMappingURL=ui-message-system.js.map